import { useState } from 'react';
import { ActionIcon, Box, Text, ThemeIcon, Tooltip } from '@mantine/core';
import { Icon3dCubeSphere, IconInfoCircle, IconUpload, IconX } from '@tabler/icons-react';
import { Dropzone, DropzoneProps, FileRejection } from '@mantine/dropzone';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { MODEL_UPLOAD_MAX_SIZE_MB } from '../constants/modelUpload';
import { PRODUCT_CARD_PREVIEW_BG } from '@/constants/productPreview';
import { colorWithAlpha } from '@/utils/colorWithAlpha';
import { getFilesFromEvent } from '@/utils/dropzoneFiles';
import classes from './UploadSurface.module.css';

function notifyDropzoneRejections(
  rejections: FileRejection[],
  maxMb: number,
  t: (key: string, options?: Record<string, unknown>) => string
) {
  if (rejections.length === 0) return;

  const errors = rejections.flatMap((rejection) => rejection.errors);

  if (errors.some((error) => error.code === 'file-too-large')) {
    notifications.show({
      title: t('dropModelZone.rejectedTitle'),
      message: t('dropModelZone.fileTooLarge', { maxMb }),
      color: 'red',
    });
    return;
  }

  if (errors.some((error) => error.code === 'file-invalid-type')) {
    notifications.show({
      title: t('dropModelZone.rejectedTitle'),
      message: t('dropModelZone.invalidType'),
      color: 'red',
    });
    return;
  }

  notifications.show({
    title: t('dropModelZone.rejectedTitle'),
    message: t('dropModelZone.rejectedGeneric'),
    color: 'red',
  });
}

type DropModelZoneProps = Partial<DropzoneProps> & {
  corporateColor?: string;
  /** Smaller drop area after files are already chosen. */
  compact?: boolean;
};

export const DropModelZone = ({
  onDrop = (files) => console.log('accepted files', files),
  onReject = (files) => console.log('rejected files', files),
  corporateColor = '#1c7ed6',
  compact = false,
  multiple = false,
  ...dropzoneProps
}: DropModelZoneProps) => {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(false);
  const mbSizeLimit = MODEL_UPLOAD_MAX_SIZE_MB;

  const handleReject = (rejections: FileRejection[]) => {
    notifyDropzoneRejections(rejections, mbSizeLimit, t);
    onReject(rejections);
  };

  const idleBorderColor = hovered ? corporateColor : colorWithAlpha(corporateColor, 0.45);
  const previewBackground = `linear-gradient(145deg, ${colorWithAlpha(corporateColor, 0.06)} 0%, ${PRODUCT_CARD_PREVIEW_BG} 42%, ${colorWithAlpha(corporateColor, 0.14)} 100%)`;

  return (
    <Dropzone
      onDrop={onDrop}
      onReject={handleReject}
      getFilesFromEvent={getFilesFromEvent}
      multiple={multiple}
      maxSize={mbSizeLimit * 1024 ** 2}
      className={`${classes.dropzone}${compact ? ` ${classes.dropzoneCompact}` : ''}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        borderColor: idleBorderColor,
        backgroundColor: hovered ? 'var(--mantine-color-default)' : PRODUCT_CARD_PREVIEW_BG,
      }}
      {...dropzoneProps}
      accept={{
        'model/gltf-binary': ['.glb'],
        'model/gltf': ['.gltf'],
        'model/gltf+json': ['.gltf'],
        'model/step': ['.step', '.stp'],
        'application/x-step': ['.step', '.stp'],
        'application/ifc': ['.ifc'],
        'application/x-ifc': ['.ifc'],
        'model/3dm': ['.3dm'],
        'model/iges': ['.iges', '.igs'],
        // Browsers often report CAD/BIM picks as octet-stream — include .ifc here.
        'application/octet-stream': [
          '.step',
          '.stp',
          '.fbx',
          '.usd',
          '.usdc',
          '.usda',
          '.usdz',
          '.ifc',
        ],
        'model/vnd.usdz+zip': ['.usdz'],
        'model/obj': ['.obj'],
        'model/x.stl-ascii': ['.stl'],
        'model/x.stl-binary': ['.stl'],
        'model/vnd.collada+xml': ['.dae'],
        'model/ply': ['.ply'],
      }}
    >
      <div className={classes.inner} style={{ pointerEvents: 'none' }}>
        <div className={classes.infoButton}>
          <Tooltip
            label={
              <div>
                <Text size="sm" mb="xs">
                  <strong>
                    {t(multiple ? 'dropModelZone.eachFileLimit' : 'dropModelZone.singleFileLimit', {
                      maxMb: mbSizeLimit,
                    })}
                  </strong>
                </Text>
                <Text size="xs" mb="xs">
                  {t('dropModelZone.appleAnimationNote')}
                </Text>
                <Text size="xs" mb="xs">
                  <strong>{t('dropModelZone.supportedFormats')}</strong>
                </Text>
                <Text size="xs" mb="xs">
                  {t('dropModelZone.meshFormats')}
                </Text>
                <Text size="xs">{t('dropModelZone.cadBimFormats')}</Text>
              </div>
            }
            multiline
            position="top"
          >
            <ActionIcon size="sm" variant="subtle" color="gray" style={{ pointerEvents: 'auto' }}>
              <IconInfoCircle size={16} />
            </ActionIcon>
          </Tooltip>
        </div>

        <div className={classes.preview} style={{ background: previewBackground }}>
          <div className={classes.previewPattern} />

          <Dropzone.Idle>
            <div
              className={classes.iconRing}
              style={{
                backgroundColor: colorWithAlpha(corporateColor, hovered ? 0.18 : 0.12),
                boxShadow: hovered
                  ? `0 0 0 10px ${colorWithAlpha(corporateColor, 0.08)}`
                  : `0 0 0 8px ${colorWithAlpha(corporateColor, 0.05)}`,
              }}
            >
              <ThemeIcon
                size={44}
                radius="xl"
                variant="filled"
                style={{ backgroundColor: corporateColor }}
              >
                <IconUpload size={24} stroke={2.5} />
              </ThemeIcon>
            </div>
          </Dropzone.Idle>

          <Dropzone.Accept>
            <div
              className={classes.stateRing}
              style={{
                backgroundColor: 'rgba(34, 197, 94, 0.14)',
                boxShadow: '0 0 0 10px rgba(34, 197, 94, 0.08)',
              }}
            >
              <ThemeIcon size={44} radius="xl" variant="filled" color="green">
                <Icon3dCubeSphere size={24} stroke={2} />
              </ThemeIcon>
            </div>
          </Dropzone.Accept>

          <Dropzone.Reject>
            <div
              className={classes.stateRing}
              style={{
                backgroundColor: 'rgba(239, 68, 68, 0.14)',
                boxShadow: '0 0 0 10px rgba(239, 68, 68, 0.08)',
              }}
            >
              <ThemeIcon size={44} radius="xl" variant="filled" color="red">
                <IconX size={24} stroke={2.5} />
              </ThemeIcon>
            </div>
          </Dropzone.Reject>
        </div>

        <Box className={classes.footer}>
          <Dropzone.Idle>
            <Text fw={600} size="sm">
              {t(
                compact
                  ? 'dropModelZone.addMoreTitle'
                  : multiple
                    ? 'dropModelZone.titleMultiple'
                    : 'dropModelZone.title'
              )}
            </Text>
            <Text size="xs" c="dimmed">
              {t(
                compact
                  ? 'dropModelZone.addMoreSubtitle'
                  : multiple
                    ? 'dropModelZone.subtitleMultiple'
                    : 'dropModelZone.subtitle'
              )}
            </Text>
            <Text size="xs" c="dimmed" mt={4}>
              {t(multiple ? 'dropModelZone.dragOrClickMultiple' : 'dropModelZone.dragOrClick')}
            </Text>
          </Dropzone.Idle>

          <Dropzone.Accept>
            <Text fw={600} size="sm" c="green.7">
              {t('dropModelZone.acceptTitle')}
            </Text>
            <Text size="xs" c="dimmed">
              {t(
                multiple ? 'dropModelZone.acceptSubtitleMultiple' : 'dropModelZone.acceptSubtitle'
              )}
            </Text>
          </Dropzone.Accept>

          <Dropzone.Reject>
            <Text fw={600} size="sm" c="red.7">
              {t('dropModelZone.rejectTitle')}
            </Text>
            <Text size="xs" c="dimmed">
              {t('dropModelZone.rejectSubtitle')}
            </Text>
          </Dropzone.Reject>
        </Box>
      </div>
    </Dropzone>
  );
};
